import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SEO, { pageSEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const [isCallbackRequest, setIsCallbackRequest] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city_state: "",
    num_travelers: "",
    preferred_departure: "",
    preferred_callback_time: "",
    message: ""
  });

  useEffect(() => {
    const departure = searchParams.get("departure");
    const action = searchParams.get("action");
    
    if (departure) {
      setFormData(prev => ({
        ...prev,
        preferred_departure: departure,
        message: action === "reserve" 
          ? `I'd like to reserve a spot for the ${departure} departure.`
          : `I'm interested in the ${departure} departure and would like more information.`
      }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        num_travelers: formData.num_travelers ? parseInt(formData.num_travelers) : null,
        is_callback_request: isCallbackRequest
      });
      
      toast.success("Inquiry sent successfully! We'll be in touch within 24 hours.");
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        city_state: "",
        num_travelers: "",
        preferred_departure: "",
        preferred_callback_time: "",
        message: ""
      });
      setIsCallbackRequest(false);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error submitting inquiry:", error);
      }
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24 md:pt-32" data-testid="contact-page">
      <SEO {...pageSEO.contact} />
      {/* Hero */}
      <section className="section-padding bg-[#243b5a] text-white">
        <div className="container-max text-center">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Let's Connect</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">
            Contact <span className="text-[#D4AF37]">Us</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Ready to start your journey to India? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-padding bg-[#F9F9F7]">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-serif text-2xl text-[#243b5a] mb-6">Get in Touch</h2>
              
              <div className="space-y-6 mb-8">
                <a href="tel:781-367-4010" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-full bg-[#243b5a]/10 flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors">
                    <Phone className="w-5 h-5 text-[#243b5a] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-[#888888]">Phone</p>
                    <p className="font-medium text-[#1A1A1A]">781-367-4010</p>
                  </div>
                </a>
                
                <a href="mailto:aaron@awakenindiatours.com" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-full bg-[#243b5a]/10 flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors">
                    <Mail className="w-5 h-5 text-[#243b5a] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-[#888888]">Email</p>
                    <p className="font-medium text-[#1A1A1A]">aaron@awakenindiatours.com</p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#243b5a]/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#243b5a]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#888888]">Serving</p>
                    <p className="font-medium text-[#1A1A1A]">NYC, NJ & East Coast</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl border border-[#D4AF37]/20">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-5 h-5 text-[#D4AF37]" />
                  <span className="font-medium text-[#243b5a]">Response Time</span>
                </div>
                <p className="text-[#4A4A4A] text-sm">
                  We respond to all inquiries within 24 hours. Prefer a call? Let us know your preferred time and we'll schedule one.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsCallbackRequest(!isCallbackRequest)}
                className={`mt-6 w-full py-4 px-6 rounded-xl border-2 transition-all ${
                  isCallbackRequest 
                    ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#243b5a]' 
                    : 'border-[#243b5a]/20 text-[#243b5a] hover:border-[#D4AF37]'
                }`}
                data-testid="schedule-call-btn"
              >
                <div className="flex items-center justify-center gap-2">
                  <Phone className="w-5 h-5" />
                  <span className="font-medium">Schedule a Call</span>
                </div>
              </button>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-8 md:p-10 shadow-soft">
                <h2 className="font-serif text-2xl text-[#243b5a] mb-6">Send Us an Inquiry</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="inquiry-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-[#1A1A1A]">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-2 form-input"
                        placeholder="Your full name"
                        data-testid="name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#1A1A1A]">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-2 form-input"
                        placeholder="your@email.com"
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-[#1A1A1A]">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-2 form-input"
                        placeholder="(555) 123-4567"
                        data-testid="phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city_state" className="text-[#1A1A1A]">City/State</Label>
                      <Input
                        id="city_state"
                        name="city_state"
                        value={formData.city_state}
                        onChange={handleChange}
                        className="mt-2 form-input"
                        placeholder="e.g. Boston, MA"
                        data-testid="city-state-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="num_travelers" className="text-[#1A1A1A]">Number of Travelers</Label>
                      <Input
                        id="num_travelers"
                        name="num_travelers"
                        type="number"
                        min="1"
                        value={formData.num_travelers}
                        onChange={handleChange}
                        className="mt-2 form-input"
                        placeholder="e.g. 2"
                        data-testid="num-travelers-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="preferred_departure" className="text-[#1A1A1A]">Preferred Departure Window</Label>
                      <Input
                        id="preferred_departure"
                        name="preferred_departure"
                        value={formData.preferred_departure}
                        onChange={handleChange}
                        className="mt-2 form-input"
                        placeholder={searchParams.get("departure") || "e.g. October 2026"}
                        data-testid="preferred-departure-input"
                      />
                    </div>
                  </div>

                  {/* Callback time field - only shown when Schedule a Call is clicked */}
                  {isCallbackRequest && (
                    <div className="animate-fade-in-up">
                      <Label htmlFor="preferred_callback_time" className="text-[#1A1A1A]">
                        Preferred Day & Time for Callback *
                      </Label>
                      <Input
                        id="preferred_callback_time"
                        name="preferred_callback_time"
                        value={formData.preferred_callback_time}
                        onChange={handleChange}
                        required={isCallbackRequest}
                        className="mt-2 form-input"
                        placeholder="e.g. Tuesday or Wednesday after 5PM"
                        data-testid="callback-time-input"
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="message" className="text-[#1A1A1A]">Tell us about your dream India experience</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="mt-2 form-input min-h-[150px]"
                      placeholder="Share your travel goals, questions, or anything else you'd like us to know..."
                      data-testid="message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="btn-gold w-full md:w-auto"
                    disabled={isSubmitting}
                    data-testid="submit-inquiry-btn"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Inquiry
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-[#888888] mt-4">
                    By submitting, you agree to receive communications about your inquiry. We never share your information.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
